'use client'

import { NavigateOptions, PrefetchOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import NextLinkOrg from 'next/link'
import { useParams, useRouter as useNextRouter } from 'next/navigation'
import recordPCEvent from 'core/utils/RecordEvent/recordPCEvent'
import TRecordEvent from 'core/utils/RecordEvent/TRecordPCEvent'
import vueUrls from '../../generatePages/urls'
import vueH5Urls from '../../../h5-ssr/generatePages/urls'
import { languages } from 'core/config/locale'

// 防止mono项目中的url跳转和vueH5Urls里的地址冲突
const filteredH5Urls = vueH5Urls.filter((item) => {
  return !item.responsive
})

// 类型定义
type NavigationTarget = '_self' | '_blank' | '_parent' | '_top'
type SelfOptions = {
  target?: NavigationTarget
  withRedirect?: boolean // 是否需要添加重定向参数
  redirectWithHostName?: boolean // 是否需要添加重定向参数，包含域名
}
type NextLinkProps<K extends keyof TRecordEvent> = React.ComponentProps<typeof NextLinkOrg> & {
  recordEventName?: K
} & {
  recordEventName?: K
  recordEventParams?: TRecordEvent[K]
}

const getNormalizedPath = (href: string) => {
  let url: URL
  try {
    // 如果是相对路径，添加一个基础域名
    url = href.startsWith('http') ? new URL(href) : new URL(href, 'http://example.com')
  } catch (e) {
    console.warn('Invalid URL:', href)
    return { path: '', isValid: false }
  }

  // 只获取路径部分，去除查询参数和hash
  let path = url.pathname
  /*
    1.如果path带有多语言前缀，需要去掉前缀
    */
  languages.forEach((lang) => {
    if (path.startsWith(`/${lang}`)) {
      path = path.replace(`/${lang}`, '')
    }
  })
  /* 
  2.如果path以/结尾，去掉结尾的/
  */
  if (path.endsWith('/')) {
    path = path.slice(0, -1)
  }

  return { path, isValid: true }
}

const checkUrlInList = (urlList: Array<{ url: string }>, path: string) => {
  return (
    urlList.findIndex(({ url }) => {
      // 不包含动态参数的直接比较
      if (!url.includes('[')) {
        return (
          url === path ||
          /* 兼容vue动态路由 */
          path.startsWith('/learn/article') ||
          path.startsWith('/marketExplore/overview') ||
          path.startsWith('/margin/trade') ||
          path.startsWith('/market/rankings') ||
          path.startsWith('/position/detail')
        )
      }
      // 包含动态参数的按段比较
      const urlArr = url.split(/\//)
      const pathArr = path.split(/\//)
      return urlArr.every((item, index) => {
        if (item.includes('[')) return true
        return item === pathArr[index]
      })
    }) !== -1
  )
}

/**
 * URL处理相关工具函数
 */
class UrlUtils {
  /**
   * 检查是否为Vue页面的URL
   * @param href - 需要检查的URL
   */
  static checkIsVueUrl(href: string): boolean {
    const { path, isValid } = getNormalizedPath(href)
    if (!isValid) return true

    return checkUrlInList(vueUrls, path) || checkUrlInList(filteredH5Urls, path)
  }

  /**
   * 检查是否为Vue H5页面的URL
   * @param href - 需要检查的URL
   */
  static checkIsVueH5Url(href: string): boolean {
    const { path, isValid } = getNormalizedPath(href)
    if (!isValid) return true

    return checkUrlInList(filteredH5Urls, path)
  }

  /**
   * 转换为H5 URL
   * @param path - 原始URL
   */
  static processMobileUrl(path: string): string {
    if (typeof window === 'undefined' || process.env.NODE_ENV === 'development') {
      return path
    }
    const origin = window.location.origin.replace('www.', 'm.').replace('wt.', 'mt.')
    return new URL(path, origin).href
  }

  /**
   * 处理URL，添加语言前缀
   * @param href - 原始URL
   * @param routePrefix - 语言前缀
   */
  static processUrl(href: string, routePrefix: string): string {
    if (href.startsWith('http') || href.startsWith('//')) {
      return href
    }
    const [basePath, queryString] = href.split('?')
    let processedPath = `/${routePrefix}/${basePath.replace(/^\//, '').replace(routePrefix, '').replace(/^\//, '')}`

    if (this.checkIsVueH5Url(href)) {
      processedPath = this.processMobileUrl(processedPath)
    }

    return queryString ? `${processedPath}?${queryString}` : processedPath
  }

  /**
   * 处理重定向参数
   * @param url - 需要处理的URL
   * @param redirectWithHostName - 重定向URL是否需要包含域名
   * @returns 处理后的URL字符串
   */
  static handleRedirect(url: string, redirectWithHostName: boolean = false): string {
    const urlObj = new URL(url, window.location.origin)
    if (!urlObj.searchParams.has('redirect')) {
      const currentLocation = redirectWithHostName
        ? window.location.href
        : window.location.pathname + window.location.search + window.location.hash
      urlObj.searchParams.append('redirect', encodeURI(currentLocation))
    }
    return urlObj.pathname + urlObj.search + urlObj.hash
  }
}

/**
 * 自定义路由钩子
 * 扩展Next.js默认路由功能
 */
export const useHCRouter = () => {
  const params = useParams()
  const router = useNextRouter()
  const routePrefix = `${params.lang}`

  return {
    ...router,
    /**
     * 页面跳转
     * @param href - 目标URL
     * @param options - 跳转选项
     */
    push(href: string, options?: NavigateOptions & SelfOptions) {
      let newHref = UrlUtils.processUrl(href, routePrefix)

      if (options?.withRedirect) {
        newHref = UrlUtils.handleRedirect(newHref, options?.redirectWithHostName)
      }

      if (UrlUtils.checkIsVueUrl(href) || options?.target) {
        window.open(newHref, options?.target || '_self')
        return
      }
      router.push(newHref, options)
    },

    /**
     * 替换当前页面
     */
    replace(href: string, options?: NavigateOptions & SelfOptions) {
      const newHref = UrlUtils.processUrl(href, routePrefix)
      if (UrlUtils.checkIsVueUrl(href) || options?.target) {
        window.location.replace(newHref)
        return
      }
      router.replace(newHref, options)
    },

    /**
     * 预加载页面
     */
    prefetch(href: string, options?: PrefetchOptions & SelfOptions) {
      const newHref = UrlUtils.processUrl(href, routePrefix)

      if (UrlUtils.checkIsVueUrl(href) || options?.target) {
        return // 不预加载Vue页面
      }
      router.prefetch(newHref, options)
    },
  }
}

/**
 * 自定义Next.js Link组件
 * 支持Vue页面链接和语言前缀
 */
export const HCNextLink = <K extends keyof TRecordEvent>(props: NextLinkProps<K>) => {
  const params = useParams()
  const routePrefix = `${params.lang}`
  // 用于处理无链接的加前缀后 会跳转
  const processIgnoreUrl = ['javascript:void(0)', 'javascript:;']
  const newHref = processIgnoreUrl.includes(props.href as string)
    ? props.href
    : UrlUtils.processUrl(props.href as string, routePrefix)

  // 从 props 中提取出需要的属性，其余的放入 restProps
  const { recordEventName, recordEventParams, onClick, href, ...restProps } = props

  const handleLinkClick = (e) => {
    onClick?.(e)
    if (!recordEventName) return
    recordPCEvent.record(recordEventName, recordEventParams)
  }

  // Vue页面使用原生a标签
  if (UrlUtils.checkIsVueUrl(href as string)) {
    return (
      <a {...restProps} href={newHref} onClick={handleLinkClick}>
        {props.children}
      </a>
    )
  }

  // Next.js页面使用Next Link
  return <NextLinkOrg {...restProps} onClick={handleLinkClick} href={newHref} />
}

export default HCNextLink
